import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import {
  Text,
  List,
  Switch,
  Divider,
  Card,
  Button,
  useTheme,
  RadioButton,
} from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '../store';
import {
  updateUserPreferences,
  selectUserPreferences,
  selectUserUpdating,
} from '../store/slices/userSlice';
import {
  updateNotificationPreferences,
  selectNotificationPreferences,
  selectNotificationUpdating,
} from '../store/slices/notificationSlice';
import {
  Settings,
  Bell,
  Palette,
  Globe,
  Shield,
  Download,
  Trash2,
} from 'lucide-react-native';
import Toast from 'react-native-toast-message';

export function SettingsScreen() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();

  const preferences = useAppSelector(selectUserPreferences);
  const notificationPreferences = useAppSelector(selectNotificationPreferences);
  const isUserUpdating = useAppSelector(selectUserUpdating);
  const isNotificationUpdating = useAppSelector(selectNotificationUpdating);

  const [selectedTheme, setSelectedTheme] = useState<'light' | 'dark' | 'auto'>(
    preferences?.theme || 'auto'
  );
  const [selectedUnits, setSelectedUnits] = useState<'metric' | 'imperial'>(
    preferences?.preferred_units || 'metric'
  );
  const [privacyLevel, setPrivacyLevel] = useState<
    'public' | 'friends' | 'private'
  >(preferences?.privacy_level || 'public');

  useEffect(() => {
    if (preferences) {
      setSelectedTheme(preferences.theme || 'auto');
      setSelectedUnits(preferences.preferred_units || 'metric');
      setPrivacyLevel(preferences.privacy_level || 'public');
    }
  }, [preferences]);

  const handlePreferenceUpdate = async (updates: any) => {
    try {
      await dispatch(updateUserPreferences(updates)).unwrap();
      Toast.show({
        type: 'success',
        text1: 'Settings updated',
      });
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to update settings',
        text2: error as string,
      });
    }
  };

  const handleNotificationUpdate = async (updates: any) => {
    try {
      await dispatch(updateNotificationPreferences(updates)).unwrap();
      Toast.show({
        type: 'success',
        text1: 'Notification settings updated',
      });
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to update notifications',
        text2: error as string,
      });
    }
  };

  const themeOptions = [
    { label: 'Light', value: 'light' },
    { label: 'Dark', value: 'dark' },
    { label: 'System Default', value: 'auto' },
  ];

  const unitOptions = [
    { label: 'Metric (°C, ml, g)', value: 'metric' },
    { label: 'Imperial (°F, fl oz, oz)', value: 'imperial' },
  ];

  const privacyOptions = [
    {
      label: 'Public',
      value: 'public',
      description: 'Anyone can see your profile',
    },
    {
      label: 'Friends Only',
      value: 'friends',
      description: 'Only followers can see your profile',
    },
    {
      label: 'Private',
      value: 'private',
      description: 'Only you can see your profile',
    },
  ];

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      contentContainerStyle={styles.scrollContent}
    >
      {/* Header */}
      <View style={styles.header}>
        <Text
          variant='headlineMedium'
          style={[styles.headerTitle, { color: theme.colors.onSurface }]}
        >
          Settings
        </Text>
        <Text
          variant='bodyMedium'
          style={{ color: theme.colors.onSurfaceVariant }}
        >
          Customize your CoffeePlexity experience
        </Text>
      </View>

      {/* Appearance Settings */}
      <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <View style={styles.sectionHeader}>
            <Palette size={24} color={theme.colors.primary} />
            <Text
              variant='titleMedium'
              style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
            >
              Appearance
            </Text>
          </View>

          <Text
            variant='bodyMedium'
            style={[
              styles.sectionDescription,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            Theme Preference
          </Text>
          <RadioButton.Group
            onValueChange={value => {
              const theme = value as 'light' | 'dark' | 'auto';
              setSelectedTheme(theme);
              handlePreferenceUpdate({ theme });
            }}
            value={selectedTheme}
          >
            {themeOptions.map(option => (
              <RadioButton.Item
                key={option.value}
                label={option.label}
                value={option.value}
                disabled={isUserUpdating}
              />
            ))}
          </RadioButton.Group>
        </Card.Content>
      </Card>

      {/* Units Settings */}
      <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <View style={styles.sectionHeader}>
            <Globe size={24} color={theme.colors.primary} />
            <Text
              variant='titleMedium'
              style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
            >
              Units & Measurements
            </Text>
          </View>

          <RadioButton.Group
            onValueChange={value => {
              const units = value as 'metric' | 'imperial';
              setSelectedUnits(units);
              handlePreferenceUpdate({ preferred_units: units });
            }}
            value={selectedUnits}
          >
            {unitOptions.map(option => (
              <RadioButton.Item
                key={option.value}
                label={option.label}
                value={option.value}
                disabled={isUserUpdating}
              />
            ))}
          </RadioButton.Group>
        </Card.Content>
      </Card>

      {/* Privacy Settings */}
      <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <View style={styles.sectionHeader}>
            <Shield size={24} color={theme.colors.primary} />
            <Text
              variant='titleMedium'
              style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
            >
              Privacy
            </Text>
          </View>

          <RadioButton.Group
            onValueChange={value => {
              const privacy = value as 'public' | 'friends' | 'private';
              setPrivacyLevel(privacy);
              handlePreferenceUpdate({ privacy_level: privacy });
            }}
            value={privacyLevel}
          >
            {privacyOptions.map(option => (
              <View key={option.value}>
                <RadioButton.Item
                  label={option.label}
                  value={option.value}
                  disabled={isUserUpdating}
                />
                <Text
                  variant='bodySmall'
                  style={[
                    styles.optionDescription,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  {option.description}
                </Text>
              </View>
            ))}
          </RadioButton.Group>
        </Card.Content>
      </Card>

      {/* Notification Settings */}
      <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <View style={styles.sectionHeader}>
            <Bell size={24} color={theme.colors.primary} />
            <Text
              variant='titleMedium'
              style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
            >
              Notifications
            </Text>
          </View>

          <List.Item
            title='Push Notifications'
            description='Receive notifications on your device'
            right={() => (
              <Switch
                value={notificationPreferences?.push_notifications || false}
                onValueChange={value =>
                  handleNotificationUpdate({ push_notifications: value })
                }
                disabled={isNotificationUpdating}
              />
            )}
          />
          <Divider />
          <List.Item
            title='Email Notifications'
            description='Receive notifications via email'
            right={() => (
              <Switch
                value={notificationPreferences?.email_notifications || false}
                onValueChange={value =>
                  handleNotificationUpdate({ email_notifications: value })
                }
                disabled={isNotificationUpdating}
              />
            )}
          />
          <Divider />
          <List.Item
            title='Recipe Likes'
            description='When someone likes your recipe'
            right={() => (
              <Switch
                value={notificationPreferences?.recipe_likes || false}
                onValueChange={value =>
                  handleNotificationUpdate({ recipe_likes: value })
                }
                disabled={isNotificationUpdating}
              />
            )}
          />
          <Divider />
          <List.Item
            title='New Followers'
            description='When someone follows you'
            right={() => (
              <Switch
                value={notificationPreferences?.new_followers || false}
                onValueChange={value =>
                  handleNotificationUpdate({ new_followers: value })
                }
                disabled={isNotificationUpdating}
              />
            )}
          />
          <Divider />
          <List.Item
            title='Creator Updates'
            description='Updates from creators you follow'
            right={() => (
              <Switch
                value={notificationPreferences?.creator_updates || false}
                onValueChange={value =>
                  handleNotificationUpdate({ creator_updates: value })
                }
                disabled={isNotificationUpdating}
              />
            )}
          />
        </Card.Content>
      </Card>

      {/* Advanced Settings */}
      <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <View style={styles.sectionHeader}>
            <Settings size={24} color={theme.colors.primary} />
            <Text
              variant='titleMedium'
              style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
            >
              Advanced
            </Text>
          </View>

          <Button
            mode='outlined'
            icon={Download}
            onPress={() => navigation.navigate('dataExport' as never)}
            style={styles.advancedButton}
          >
            Export My Data
          </Button>

          <Button
            mode='outlined'
            icon={Trash2}
            onPress={() => navigation.navigate('deleteAccount' as never)}
            style={[styles.advancedButton, { borderColor: theme.colors.error }]}
            textColor={theme.colors.error}
          >
            Delete Account
          </Button>
        </Card.Content>
      </Card>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  header: {
    marginBottom: 24,
  },
  headerTitle: {
    fontWeight: '700',
    marginBottom: 4,
  },
  card: {
    marginBottom: 16,
    elevation: 2,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    marginLeft: 12,
    fontWeight: 'bold',
  },
  sectionDescription: {
    marginBottom: 8,
    fontWeight: '500',
  },
  optionDescription: {
    marginLeft: 56,
    marginTop: -8,
    marginBottom: 8,
  },
  advancedButton: {
    marginBottom: 12,
  },
});
